import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, ImagesSquare, CheckCircle, Warning, ArrowRight, ArrowLeft,
         House, User as UserIcon, X, FloppyDisk, Clock } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = [
  { key: "excellent", label: "Excellent", color: "bg-emerald-600 hover:bg-emerald-700" },
  { key: "good",      label: "Good",      color: "bg-sky-600 hover:bg-sky-700" },
  { key: "fair",      label: "Fair",      color: "bg-amber-500 hover:bg-amber-600" },
  { key: "poor",      label: "Poor",      color: "bg-red-600 hover:bg-red-700" },
  { key: "damaged",   label: "Damaged",   color: "bg-red-900 hover:bg-red-950" },
  { key: "na",        label: "N/A",       color: "bg-slate-400 hover:bg-slate-500" },
];

export default function TenantInspectionPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [roomIdx, setRoomIdx] = useState(0);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/public/tenant-inspection/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load inspection"));
  }, [token]);

  // Compute rooms + progress ABOVE any early return so hooks always run in the same order.
  const rooms = data?.rooms || [];
  const responses = data?.responses || {};
  const overallProgress = useMemo(() => {
    const total = rooms.reduce((n, r) => n + (r.items?.length || 0), 0);
    if (total === 0) return 0;
    const done = rooms.reduce((n, r) => n + (r.items || []).filter(it => responses[`${r.key}.${it.key}`]?.condition).length, 0);
    return done / total;
  }, [rooms, responses]);

  if (err) return <ErrorScreen msg={err}/>;
  if (!data) return <LoadingScreen/>;

  const isCompleted = data.status === "completed";
  const roomProgress = (room) => {
    const items = room.items || [];
    if (items.length === 0) return 1;
    const done = items.filter(it => data.responses[`${room.key}.${it.key}`]?.condition).length;
    return done / items.length;
  };
  const allAnswered = overallProgress >= 1;

  if (isCompleted) return <DoneScreen data={data}/>;
  if (completing) return <CompleteScreen data={data} token={token} onCancel={() => setCompleting(false)}
                          onDone={completed => setData(d => ({ ...d, status: "completed", completed_at: completed.completed_at }))}/>;

  const currentRoom = rooms[roomIdx];
  return (
    <div className="min-h-screen bg-slate-100" data-testid="tenant-inspection-portal">
      <PortalHeader data={data}/>
      <div className="max-w-2xl mx-auto px-3 pb-32">
        {/* Overall progress */}
        <div className="mt-3 bg-white border-2 border-[#0f172a] p-3" data-testid="overall-progress">
          <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-500">
            <span>Overall Progress</span>
            <span>{Math.round(overallProgress * 100)}%</span>
          </div>
          <div className="mt-1 h-2 bg-slate-200 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-cyan-500 transition-all" style={{ width: `${Math.round(overallProgress * 100)}%` }}/>
          </div>
        </div>

        {/* Room tabs */}
        <div className="mt-3 flex gap-1 overflow-x-auto pb-1" data-testid="room-tabs">
          {rooms.map((r, i) => {
            const p = roomProgress(r);
            return (
              <button key={r.key} onClick={() => setRoomIdx(i)} data-testid={`room-tab-${r.key}`}
                className={`flex-shrink-0 px-3 h-9 border-2 font-black uppercase text-xs tracking-wider ${
                  i === roomIdx ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white text-slate-700 border-slate-300"
                }`}>
                {r.name}
                <span className={`ml-2 inline-block h-2 w-2 rounded-full ${p >= 1 ? "bg-emerald-500" : p > 0 ? "bg-amber-500" : "bg-slate-300"}`}/>
              </button>
            );
          })}
        </div>

        {/* Current room items */}
        {currentRoom && (
          <div className="mt-4" data-testid={`room-${currentRoom.key}`}>
            <h2 className="font-display font-black text-2xl uppercase">{currentRoom.name}</h2>
            <div className="mt-3 space-y-3">
              {currentRoom.items.map(item => (
                <ItemCard key={item.key} token={token} room={currentRoom} item={item}
                  initial={data.responses[`${currentRoom.key}.${item.key}`] || null}
                  onSaved={resp => setData(d => ({ ...d, responses: { ...d.responses, [`${currentRoom.key}.${item.key}`]: resp } }))}/>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom nav */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-[#0f172a] p-3" data-testid="bottom-nav">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <button onClick={() => setRoomIdx(i => Math.max(0, i - 1))} disabled={roomIdx === 0}
            data-testid="prev-room"
            className="h-11 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest disabled:opacity-40 inline-flex items-center gap-1">
            <ArrowLeft size={14}/> Prev
          </button>
          {roomIdx < rooms.length - 1 ? (
            <button onClick={() => setRoomIdx(i => Math.min(rooms.length - 1, i + 1))}
              data-testid="next-room"
              className="flex-1 h-11 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
              Next Room <ArrowRight size={14}/>
            </button>
          ) : (
            <button onClick={() => setCompleting(true)} disabled={!allAnswered}
              data-testid="complete-btn"
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
              <CheckCircle weight="fill" size={14}/> {allAnswered ? "Submit Inspection" : "Answer All Items First"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalHeader({ data }) {
  return (
    <header className="bg-[#0f172a] text-white">
      <div className="max-w-2xl mx-auto px-3 py-4 flex items-center gap-3">
        <div className="h-9 w-9 bg-white flex items-center justify-center"><Logo size={22}/></div>
        <div className="flex-1">
          <div className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">{data.crew_name} · Inspection</div>
          <div className="font-display font-black text-lg leading-tight" data-testid="portal-template-name">{data.template_name}</div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-3 pb-3 text-xs space-y-0.5">
        <div className="inline-flex items-center gap-1"><House size={12}/> {data.property_address}{data.unit ? ` · Unit ${data.unit}` : ""}</div>
        <div className="inline-flex items-center gap-1 ml-3"><UserIcon size={12}/> {data.tenant_name}</div>
      </div>
    </header>
  );
}

function ItemCard({ token, room, item, initial, onSaved }) {
  const [condition, setCondition] = useState(initial?.condition || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [photos, setPhotos] = useState(initial?.photos || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const markDirty = () => setDirty(true);

  // Auto-save 800ms after last change once a condition is picked
  useEffect(() => {
    if (!dirty || !condition) return;
    const t = setTimeout(() => save(), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, notes, photos, dirty]);

  const save = async () => {
    if (!condition) return;
    setSaving(true);
    try {
      await axios.post(`${BASE}/api/public/tenant-inspection/${token}/item`, {
        room_key: room.key, item_key: item.key, condition, notes,
        photo_paths: photos.map(p => p.path),
      });
      const resp = { condition, notes, photos, updated_at: new Date().toISOString() };
      onSaved(resp);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      const newPhotos = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${BASE}/api/public/tenant-inspection/${token}/photo`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        newPhotos.push({ path: r.data.path, uploaded_at: new Date().toISOString() });
      }
      setPhotos(p => [...p, ...newPhotos]);
      setDirty(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"));
    } finally { setUploading(false); }
  };

  const removePhoto = (i) => { setPhotos(p => p.filter((_, idx) => idx !== i)); setDirty(true); };

  const photoUrl = (path) => `${BASE}/api/public/tenant-inspection/${token}/photo/${path}`;

  return (
    <div className="bg-white border-2 border-[#0f172a] p-3" data-testid={`item-${room.key}-${item.key}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 font-bold text-sm leading-tight">{item.label}</div>
        {saving && <Clock size={14} className="text-amber-500 animate-pulse mt-1"/>}
        {!saving && condition && !dirty && <CheckCircle weight="fill" size={14} className="text-emerald-500 mt-1"/>}
      </div>

      {/* Condition buttons */}
      <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid={`condition-${room.key}-${item.key}`}>
        {CONDITIONS.map(c => (
          <button key={c.key}
            onClick={() => { setCondition(c.key); markDirty(); }}
            data-testid={`cond-${c.key}-${room.key}-${item.key}`}
            className={`h-9 font-black uppercase text-[10px] tracking-widest text-white transition ${
              condition === c.key ? c.color : "bg-slate-200 text-slate-500 hover:bg-slate-300"
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Notes */}
      {item.allows_notes && (
        <textarea value={notes}
          onChange={e => { setNotes(e.target.value); markDirty(); }}
          data-testid={`notes-${room.key}-${item.key}`}
          placeholder="Notes (optional)…" rows={2}
          className="mt-2 w-full px-2 py-1.5 border-2 border-slate-300 text-xs font-medium"/>
      )}

      {/* Photos */}
      <div className="mt-2">
        <div className="flex gap-2">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only"
            onChange={e => { if (e.target.files?.length) { uploadFiles(Array.from(e.target.files)); e.target.value = ""; }}}/>
          <input ref={galleryRef} type="file" accept="image/*" multiple className="sr-only"
            onChange={e => { if (e.target.files?.length) { uploadFiles(Array.from(e.target.files)); e.target.value = ""; }}}/>
          <button onClick={() => cameraRef.current?.click()} disabled={uploading}
            data-testid={`camera-${room.key}-${item.key}`}
            className="flex-1 h-10 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1">
            <Camera weight="fill" size={13}/> {uploading ? "Uploading…" : "Take Photo"}
          </button>
          <button onClick={() => galleryRef.current?.click()} disabled={uploading}
            data-testid={`gallery-${room.key}-${item.key}`}
            className="flex-1 h-10 border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1">
            <ImagesSquare weight="fill" size={13}/> Gallery
          </button>
        </div>
        {item.requires_photo && photos.length === 0 && (
          <div className="mt-1 text-[10px] font-bold uppercase text-amber-600 inline-flex items-center gap-1">
            <Warning size={11}/> Photo suggested
          </div>
        )}
        {photos.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {photos.map((p, i) => (
              <div key={i} className="relative flex-shrink-0">
                <img src={photoUrl(p.path)} alt="" data-testid={`photo-${room.key}-${item.key}-${i}`}
                  className="h-20 w-20 object-cover border-2 border-[#0f172a]"/>
                <button onClick={() => removePhoto(i)} data-testid={`rm-photo-${room.key}-${item.key}-${i}`}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-600 text-white flex items-center justify-center rounded-full">
                  <X size={10}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CompleteScreen({ data, token, onCancel, onDone }) {
  const [sig, setSig] = useState(data.tenant_name || "");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!sig.trim()) { toast.error("Type your full name to sign"); return; }
    if (!ack) { toast.error("Acknowledgment required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/api/public/tenant-inspection/${token}/complete`,
        { signature_name: sig.trim(), signature_acknowledgment: true });
      onDone(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="complete-screen">
      <PortalHeader data={data}/>
      <div className="max-w-2xl mx-auto px-3 py-6 pb-24">
        <h2 className="font-display font-black text-2xl uppercase">Sign & Submit</h2>
        <p className="mt-2 text-sm text-slate-700">
          By submitting, you confirm the condition ratings, notes, and photos above accurately reflect the property at the time of inspection.
        </p>
        <div className="mt-5 bg-white border-2 border-[#0f172a] p-4 space-y-3">
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Type your full name to sign</div>
            <input value={sig} onChange={e => setSig(e.target.value)}
              data-testid="signature-input"
              className="w-full h-12 px-3 border-2 border-[#0f172a] font-display font-black text-2xl italic"/>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)}
              data-testid="ack-checkbox"
              className="mt-0.5 h-4 w-4"/>
            <span className="text-xs font-medium text-slate-700">
              I acknowledge that this record will be shared with the property management team for their files.
            </span>
          </label>
        </div>
      </div>
      <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-[#0f172a] p-3">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          <button onClick={onCancel} disabled={busy}
            data-testid="cancel-complete"
            className="h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest">
            Back
          </button>
          <button onClick={submit} disabled={busy || !sig.trim() || !ack}
            data-testid="submit-complete"
            className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
            <FloppyDisk weight="fill" size={14}/> {busy ? "Submitting…" : "Submit Inspection"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DoneScreen({ data }) {
  const isInternal = data.audience === "internal";
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4" data-testid="done-screen">
      <div className="max-w-md w-full bg-white border-2 border-[#0f172a] p-8 text-center">
        <div className="h-16 w-16 mx-auto bg-emerald-600 text-white rounded-full flex items-center justify-center">
          <CheckCircle weight="fill" size={40}/>
        </div>
        <h1 className="mt-4 font-display font-black text-3xl uppercase">Thank You!</h1>
        <p className="mt-2 text-sm text-slate-700">
          {isInternal
            ? <>Inspection submitted to <b>{data.crew_name}</b>. You can close this page.</>
            : <>Your inspection has been submitted to <b>{data.crew_name}</b>. You can close this page.</>}
        </p>
        <div className="mt-4 text-xs font-mono text-slate-500">
          Submitted: {data.completed_at ? new Date(data.completed_at).toLocaleString() : "—"}
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <div className="text-slate-500 font-black uppercase tracking-widest">Loading…</div>
  </div>;
}

function ErrorScreen({ msg }) {
  return <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
    <div className="max-w-md w-full bg-white border-2 border-red-500 p-6 text-center">
      <Warning weight="fill" size={40} className="mx-auto text-red-500"/>
      <div className="mt-3 font-display font-black text-2xl uppercase">Can't Load Inspection</div>
      <div className="mt-2 text-sm text-slate-700">{msg}</div>
    </div>
  </div>;
}
